import { createFileRoute } from "@tanstack/react-router"
import { Button, cn } from "@aix/ui"
import { useMemo, useState } from "react"
import type { CSSProperties } from "react"
import MarketingLayout from "~/layouts/MarketingLayout"
import { ArrowRight02Icon } from "@hugeicons-pro/core-solid-rounded"
import { HugeiconsIcon } from "@hugeicons/react"
import CustomHead from "~/components/SEOHead/CustomHead"
import { getPageSEO } from "~/utils/seo"
import { TemplateCard } from "./-TemplateCard"
import { TemplateGalleryFilters } from "./-TemplateGalleryFilters"
import { DEFAULT_TEMPLATE_FILTERS, filterTemplates } from "./-templateFilters"
import type { TTemplateCard } from "./-TemplateCard"
import type { ITemplateFilterable, ITemplateFilters } from "./-templateFilters"

type TGalleryTemplate = TTemplateCard &
  ITemplateFilterable & {
    id: string
  }

const TEMPLATES = [
  {
    destination: "/templates/reddit-story",
    id: "reddit-story",
    length: "Short",
    name: "Reddit Story",
    platform: "TikTok",
    status: "supported",
    style: "Narrative",
  },
  {
    destination: "/templates/fake-text",
    id: "fake-text",
    length: "Short",
    name: "Fake Text",
    platform: "TikTok",
    status: "supported",
    style: "Conversation",
  },
  {
    destination: "/templates/ai-story",
    id: "ai-story",
    length: "Medium",
    name: "AI Story",
    platform: "YouTube Shorts",
    status: "supported",
    style: "Narrative",
  },
  {
    destination: "/templates/pov-story",
    id: "pov-story",
    length: "Short",
    name: "POV Story",
    platform: "Instagram Reels",
    status: "supported",
    style: "POV",
  },
  {
    destination: "/templates/genz-avatars",
    id: "genz-avatars",
    length: "Medium",
    name: "GenZ Avatars",
    platform: "TikTok",
    status: "supported",
    style: "UGC",
  },
  {
    id: "product-demo",
    length: "Medium",
    name: "Product Demo",
    platform: "Instagram Reels",
    status: "coming-soon",
    style: "Explainer",
  },
] as const satisfies readonly TGalleryTemplate[]

function Templates() {
  const [filters, setFilters] = useState<ITemplateFilters>(DEFAULT_TEMPLATE_FILTERS)
  const filteredTemplates = useMemo(() => filterTemplates(TEMPLATES, filters), [filters])
  const trendingTemplates = TEMPLATES.slice(0, 4) // Example trending

  const templatesSEO = getPageSEO("templates")

  return (
    <>
      <CustomHead
        title={templatesSEO.title}
        description={templatesSEO.description}
        keywords={templatesSEO.keywords}
        pageType={templatesSEO.pageType}
        url="https://vidmax.ai/templates"
      />

      <div className="min-h-screen text-foreground">
        <div className="flex flex-col pt-3 lg:pt-24">
          {/* Hero Section */}
          <section className="py-16 text-center sm:py-24">
            <div className="max-w-3xl px-6 mx-auto lg:px-8">
              <h1 className="text-4xl font-bold tracking-tight sm:text-6xl">Steal our looks.</h1>
              <p className="mt-6 text-lg leading-8 text-muted-foreground">
                110+ templates. One-click makeovers for any niche.
              </p>
            </div>
          </section>

          {/* Filterable Gallery */}
          <section className="py-16 sm:py-24">
            <div className="px-6 mx-auto max-w-7xl lg:px-8">
              <TemplateGalleryFilters
                templates={TEMPLATES}
                value={filters}
                onChange={setFilters}
                onClear={() => setFilters(DEFAULT_TEMPLATE_FILTERS)}
              />
              {filteredTemplates.length === 0 ? (
                <div
                  className="rounded-lg border border-dashed border-border p-10 text-center"
                  role="status"
                  aria-live="polite"
                >
                  <h2 className="text-lg font-semibold">No templates match those filters.</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Try a different search or clear the filters to browse the full gallery.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-6"
                    onClick={() => setFilters(DEFAULT_TEMPLATE_FILTERS)}
                  >
                    Clear filters
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {filteredTemplates.map((template) => (
                    <TemplateCard key={template.id} template={template} />
                  ))}
                </div>
              )}
              <p className="mt-4 text-xs text-center text-muted-foreground">
                Choose any available format to start creating.
              </p>
            </div>
          </section>

          {/* Top Picks Carousel */}
          <section className="py-16 overflow-hidden sm:py-24 bg-secondary">
            <div className="px-6 mx-auto max-w-7xl lg:px-8">
              <h2 className="mb-8 text-2xl font-semibold text-center">
                <span role="img" aria-label="fire">
                  🔥
                </span>{" "}
                Trending Templates
              </h2>
              {/* Auto-scrolling Row Placeholder */}
              <div className="flex pb-4 -mb-4 space-x-4 overflow-x-auto scrollbar-hide">
                {/* Needs proper carousel implementation */}
                {trendingTemplates.map((template) => (
                  <div key={template.id} className="min-w-[150px] sm:min-w-[200px] flex-shrink-0">
                    <TemplateCard template={template} />
                  </div>
                ))}
                {/* Duplicate for seamless loop effect */}
                {trendingTemplates.map((template) => (
                  <div key={`${template.id}-clone`} className="min-w-[150px] sm:min-w-[200px] flex-shrink-0">
                    <TemplateCard template={template} />
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-center text-muted-foreground">Auto-scrolling row placeholder.</p>
            </div>
          </section>

          {/* Deep-dive Banner */}
          <section className="py-16 sm:py-24">
            <div className="px-6 mx-auto max-w-7xl lg:px-8">
              <div className="grid items-center grid-cols-1 gap-12 p-8 border rounded-lg lg:grid-cols-2 bg-secondary border-border">
                {/* Left: Video Mockup */}
                <div className="border rounded-lg aspect-video bg-muted border-border">
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    [Video Mockup: Reddit Stories Template]
                  </div>
                </div>
                {/* Right: Copy */}
                <div>
                  <h3 className="text-2xl font-semibold">Case Study: Viral Success</h3>
                  <p className="mt-4 text-muted-foreground">
                    Learn how the &apos;Reddit Stories&apos; template helped one creator hit{" "}
                    <span className="font-medium text-primary">1.8 Million views</span> in just 3 weeks by simplifying
                    their workflow.
                  </p>
                  <div className="mt-6">
                    <Button variant="link" className="h-auto p-0 text-primary hover:text-primary/80">
                      Read the full story <HugeiconsIcon icon={ArrowRight02Icon} size={16} className="ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* User-generated Templates Callout */}
          <section className="py-16 sm:py-24 bg-secondary">
            <div className="max-w-2xl px-6 mx-auto text-center lg:px-8">
              <h2 className="text-2xl font-semibold">Become a VidMax Designer</h2>
              <p className="mt-4 text-muted-foreground">
                Got a knack for viral video styles? Share your own template with the community and earn credits for
                every use.
              </p>
              <div className="mt-8">
                <Button variant="outline">Learn More & Submit</Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Designers wanted: share your template → get paid in credits.
              </p>
            </div>
          </section>

          {/* CTA Strip */}
          <section className="py-12 bg-gradient-to-r from-primary to-purple-600 text-primary-foreground">
            <div className="max-w-4xl px-6 mx-auto text-center lg:px-8">
              <h2 className="text-xl font-semibold">Create with a template now — try VidMax for $1!</h2>
              <div className="mt-6">
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-violet-700 hover:bg-gray-100"
                  onClick={() => {
                    /* noop */
                  }}
                >
                  Try a Template for $1 →
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  )
}

function TemplatesPage() {
  return (
    <MarketingLayout
      className={cn("bg-background")}
      logoTheme="dark"
      style={
        {
          "--background": "0 0% 0%",
          "--foreground": "0 0% 100%",
          "--secondary": "0 0% 12%",
          "--muted-foreground": "0 0% 50%",
          "--primary-foreground": "0 0% 100%",
          "--muted": "0 0% 12%",
          "--border": "0 0% 8%",
        } as CSSProperties
      }
    >
      <Templates />
    </MarketingLayout>
  )
}

export const Route = createFileRoute("/templates/")({
  component: TemplatesPage,
})
